<template>
    <div v-if="!loading && prompts.length" class="explore-tab">
      <PromptCard
        v-for="prompt in prompts"
        :key="prompt.id"
        :prompt="prompt"
        @select="openModal"
      />
    </div>
    <div v-if="allPrompts?.next">
      <button @click="loadMore" class="load-button">More</button>
    </div>

    <div v-if="isModalOpen" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <img :src="selectedPrompt?.image_url" alt="Full size image" class="modal-image"/>
        <div class="modal-buttons">
          <button @click="printImage" class="print-btn">Print</button>
          <button @click="handleDelete" class="delete-btn">Delete</button>
          <button @click="closeModal" class="close-btn">Close</button>
        </div>
      </div>
    </div>
  </template>

  <script setup lang="ts">
  import { ref, onMounted, computed } from 'vue'
  import PromptCard from './PromptCard.vue'
  import { useAuthStore } from '../stores/auth'
  import { usePromptStore } from '../stores/prompts'
  import Prompt from '@/types/Prompt'
  import PaginatedPrompt from '@/types/PaginatedPrompt'
  import { PrintPhoto } from '@/service/services'

  const loading = ref(true)
  const offset = ref(0)
  const promptStore = usePromptStore()
  const authStore = useAuthStore()

  const isModalOpen = ref(false)
  const selectedPrompt = ref<Prompt | null>(null)

  const prompts = computed<Prompt[]>(() => promptStore.allPrompts?.result ?? [])
  const allPrompts = computed<PaginatedPrompt | undefined>(() => promptStore.allPrompts)

  const getAllPrompts = async () => {
    loading.value = true
    offset.value = 0
    await promptStore.fetchAllPrompts(authStore.session?.access_token ?? '', 0)
    loading.value = false
  }

  const loadMore = async () => {
    offset.value += 10
    await promptStore.fetchAllPrompts(authStore.session?.access_token ?? '', offset.value)
  }

  const openModal = (prompt: Prompt) => {
    selectedPrompt.value = prompt
    isModalOpen.value = true
  }

  const closeModal = () => {
    isModalOpen.value = false
    selectedPrompt.value = null
  }

  const printImage = () => {
    if (selectedPrompt.value?.image_url) {
      PrintPhoto(selectedPrompt.value.image_url)
    }
  }

  const handleDelete = async () => {
    if (!selectedPrompt.value) return
    await promptStore.deletePrompt(selectedPrompt.value.uuid, selectedPrompt.value.image_url)
    closeModal()
    await getAllPrompts()
  }

  onMounted(() => {
    getAllPrompts()
  })

</script>

<style scoped>

.explore-tab {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  padding: 0;
  max-width: 100%;
  box-sizing: border-box;
  margin-top: 15px;
  margin-bottom: 10px;
  border-bottom: 3px solid transparent
}

.load-button{
    border: none;
    border-radius: 0px 0px 10px 10px;
    /* background-color: aquamarine; */
    width: 100%;
    padding: 10px;
    font-size: 20px;
    font-weight: 700;
    color:rgb(92, 140, 231);

}

.load-button:hover{
    background-color: #f0f8f0;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
  box-sizing: border-box;
}

.modal-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background-color: white;
  padding: 15px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
}

.modal-image {
  max-width: 100%;
  max-height: calc(90vh - 100px);
  object-fit: contain;
  margin-bottom: 15px;
  border-radius: 8px;
}

.modal-buttons {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
  margin-top: 10px;
}

.modal-buttons button {
  padding: 12px 16px;
  font-size: 14px;
  cursor: pointer;
  border: none;
  border-radius: 6px;
  flex: 1;
  margin: 0;
  font-weight: 600;
  transition: all 0.3s ease;
}

.modal-buttons .print-btn {
  background-color: #6dd496;
  color: #2d5a2d;
}

.modal-buttons .print-btn:hover {
  background-color: #5bc482;
}

.modal-buttons .delete-btn {
  background-color: #de6767;
  color: white;
}

.modal-buttons .delete-btn:hover {
  background-color: #d45454;
}

.modal-buttons .close-btn {
  background-color: #e7ea80;
  color: #5a5d2d;
}

.modal-buttons .close-btn:hover {
  background-color: #e0e36d;
}

</style>
